import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaAngleLeft, FaAngleRight, FaAngleDoubleLeft, FaAngleDoubleRight, FaChevronRight, FaCheck, FaHome } from 'react-icons/fa';

const BLOCK = 'LStudio__app_kit';

/*
    🔥 COMPONENTES DE NAVEGACIÓN
*/

/*
    🔥 PESTAÑAS

    ⚠️ Son botones con role="tab" cuando la pestaña es un cambio de vista
       dentro de la misma pantalla.

       Si la pestaña ES una ruta, pasale `to` en el item: se dibuja como
       <Link role="tab">, sobrevive al recargado y se puede compartir.

    Las flechas del teclado mueven entre pestañas: es lo que espera un
    lector de pantalla de un tablist.
*/
export const Tabs = ({
    items = [], value, onChange,   // item: { value, label, icon, badge, to }
    variant = 'line',              // line | pill | enclosed
    isFullWidth = false,
}) => {

    const REF__useRefList = useRef(null);

    const onKeyDown = (event) => {

        const ENABLED = items.filter((i) => !i.isDisabled);
        const POS = ENABLED.findIndex((i) => i.value === value);

        if (event.key === 'ArrowRight') {
            event.preventDefault();
            onChange?.(ENABLED[(POS + 1) % ENABLED.length].value);
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            onChange?.(ENABLED[POS <= 0 ? ENABLED.length - 1 : POS - 1].value);
        }

        if (event.key === 'Home') { event.preventDefault(); onChange?.(ENABLED[0].value); }
        if (event.key === 'End') { event.preventDefault(); onChange?.(ENABLED[ENABLED.length - 1].value); }
    };

    return (
        <div ref={REF__useRefList} className={`${BLOCK}__tabs`}
            data-variant={variant} data-full={isFullWidth}
            role="tablist" onKeyDown={onKeyDown} >
            {
                items.map((item) => {

                    const CONTENT = (
                        <>
                            {item.icon && <span className={`${BLOCK}__tab_icon`}>{item.icon}</span>}

                            <span>{item.label}</span>

                            {
                                item.badge !== undefined && (
                                    <span className={`${BLOCK}__tab_badge`}>{item.badge}</span>
                                )
                            }
                        </>
                    );

                    const PROPS = {
                        role: 'tab',
                        className: `${BLOCK}__tab`,
                        'data-on': value === item.value,
                        'aria-selected': value === item.value,
                        tabIndex: value === item.value ? 0 : -1,
                    };

                    if (item.to && !item.isDisabled) {
                        return (
                            <Link key={item.value} {...PROPS} to={item.to}
                                onClick={() => onChange?.(item.value)} >
                                {CONTENT}
                            </Link>
                        );
                    }

                    return (
                        <button key={item.value} type="button" {...PROPS}
                            disabled={item.isDisabled}
                            onClick={() => onChange?.(item.value)} >
                            {CONTENT}
                        </button>
                    );
                })
            }
        </div>
    );
};

/*
    🔥 MIGAS DE PAN

    El último item NO es un link: representa dónde estás parado, y un
    link a la página actual confunde a los lectores de pantalla.
*/
export const Breadcrumbs = ({
    items = [], separator = <FaChevronRight />,
    homeIcon = <FaHome />, maxVisible = 0,
}) => {

    // Con rutas muy profundas se colapsa el medio
    const SHOWN = maxVisible > 0 && items.length > maxVisible
        ? [items[0], { label: '…', isEllipsis: true }, ...items.slice(-(maxVisible - 2))]
        : items;

    return (
        <nav className={`${BLOCK}__crumbs`} aria-label="Ruta de navegación">
            <ol>
                {
                    SHOWN.map((item, index) => {

                        const IS_LAST = index === SHOWN.length - 1;

                        return (
                            <li key={item.href ?? item.label ?? index}>

                                {index > 0 && (
                                    <span className={`${BLOCK}__crumbs_sep`} aria-hidden="true">{separator}</span>
                                )}

                                {
                                    item.isEllipsis
                                        ? <span className={`${BLOCK}__crumbs_ellipsis`}>…</span>
                                        : IS_LAST
                                            ? <span className={`${BLOCK}__crumbs_current`} aria-current="page">{item.label}</span>
                                            : (
                                                <a href={item.href} className={`${BLOCK}__crumbs_link`}
                                                    onClick={item.onClick} >
                                                    {index === 0 && homeIcon && (
                                                        <span aria-hidden="true">{homeIcon}</span>
                                                    )}
                                                    {item.label}
                                                </a>
                                            )
                                }

                            </li>
                        );
                    })
                }
            </ol>
        </nav>
    );
};

/*
    🔥 PAGINACIÓN

    ⚠️ La ventana de números se calcula para que el botón activo quede
       centrado siempre que se pueda. Si no, al pasar de página los
       números saltan de lugar y el usuario pierde la referencia.
*/
const onGetPages = (current, total, window = 5) => {

    if (total <= window) return Array.from({ length: total }, (_, i) => i + 1);

    const HALF = Math.floor(window / 2);

    let start = Math.max(1, current - HALF);
    let end = start + window - 1;

    if (end > total) {
        end = total;
        start = end - window + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

export const Pagination = ({
    page = 1, total = 1, onChange,
    window = 5, showEdges = true, showInfo = false,
    totalItems = 0, perPage = 0,
}) => {

    if (total <= 1 && !showInfo) return null;

    const PAGES = onGetPages(page, total, window);

    const FROM = (page - 1) * perPage + 1;
    const TO = Math.min(page * perPage, totalItems);

    return (
        <nav className={`${BLOCK}__pagination`} aria-label="Paginación">

            {
                showInfo && totalItems > 0 && (
                    <span className={`${BLOCK}__pagination_info`}>
                        {FROM}–{TO} de {totalItems}
                    </span>
                )
            }

            <div className={`${BLOCK}__pagination_pages`}>

                {
                    showEdges && (
                        <button type="button" aria-label="Primera página"
                            disabled={page <= 1} onClick={() => onChange?.(1)} >
                            <FaAngleDoubleLeft />
                        </button>
                    )
                }

                <button type="button" aria-label="Anterior"
                    disabled={page <= 1} onClick={() => onChange?.(page - 1)} >
                    <FaAngleLeft />
                </button>

                {PAGES[0] > 1 && <span className={`${BLOCK}__pagination_gap`}>…</span>}

                {
                    PAGES.map((n) => (
                        <button key={n} type="button" data-on={n === page}
                            aria-current={n === page ? 'page' : undefined}
                            onClick={() => onChange?.(n)} >
                            {n}
                        </button>
                    ))
                }

                {PAGES[PAGES.length - 1] < total && <span className={`${BLOCK}__pagination_gap`}>…</span>}

                <button type="button" aria-label="Siguiente"
                    disabled={page >= total} onClick={() => onChange?.(page + 1)} >
                    <FaAngleRight />
                </button>

                {
                    showEdges && (
                        <button type="button" aria-label="Última página"
                            disabled={page >= total} onClick={() => onChange?.(total)} >
                            <FaAngleDoubleRight />
                        </button>
                    )
                }

            </div>

        </nav>
    );
};

/*
    🔥 PASOS — para asistentes de varias pantallas

    Los pasos ya completados son clickeables; los futuros no. Dejar
    saltar hacia adelante permite llegar al final sin completar lo
    anterior, que es justo lo que un asistente trata de evitar.
*/
export const Stepper = ({
    steps = [], current = 0, onChange = null,
    orientation = 'horizontal',    // horizontal | vertical
}) => (

    <ol className={`${BLOCK}__stepper`} data-orientation={orientation}>
        {
            steps.map((step, index) => {

                const IS_DONE = index < current;
                const IS_CURRENT = index === current;
                const CAN_CLICK = onChange && IS_DONE;

                return (
                    <li key={step.value ?? index} className={`${BLOCK}__step`}
                        data-done={IS_DONE} data-current={IS_CURRENT}
                        aria-current={IS_CURRENT ? 'step' : undefined} >

                        <button type="button" className={`${BLOCK}__step_button`}
                            disabled={!CAN_CLICK} onClick={() => CAN_CLICK && onChange(index)} >

                            <span className={`${BLOCK}__step_dot`}>
                                {IS_DONE ? <FaCheck /> : index + 1}
                            </span>

                            <span className={`${BLOCK}__step_text`}>
                                <span className={`${BLOCK}__step_label`}>{step.label}</span>
                                {step.description && (
                                    <span className={`${BLOCK}__step_desc`}>{step.description}</span>
                                )}
                            </span>

                        </button>

                        {index < steps.length - 1 && <span className={`${BLOCK}__step_line`} aria-hidden="true" />}

                    </li>
                );
            })
        }
    </ol>
);
